// OVO-101 — Sites tab on PartnerDetail (Address Book).
//
// Three things in one component:
//   1. Catalog table — list / search / create / edit / delete Sites
//      for the partner.
//   2. "Discover Sites" button → modal that scans the partner's
//      existing orders, proposes candidate Sites (with the same
//      partner-code-stripping logic as the Visits-by-client stats),
//      lets the operator edit name/address inline + uncheck rows,
//      then bulk-saves the validated selection.
//   3. Add/edit modal — single-Site form with name, address, optional
//      phone + notes + archived flag.
//
// Resolution at order-create time is server-side (exact case-insensitive
// (partnerId, name, address) match → populates Order.pickup/deliverySiteId).
// No auto-create of Sites; Discover is the operator's intentional path
// to populate the catalog from observed traffic.
//
// Hidden for partner viewers (admin-only; tab def carries `_hideForViewer`).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Upload,
} from 'antd';
import AddressInput from '../components/Inputs/AddressInput';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CompassOutlined,
  LinkOutlined,
  WarningOutlined,
  MergeCellsOutlined,
  PictureOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  listSitesForPartner,
  createSite,
  updateSite,
  deleteSite,
  discoverSites,
  bulkCreateSites,
  mergeSites,
  getMergeSuggestions,
  bulkDeleteSites,
  backfillPartnerSites,
  listSiteFiles,
  addSiteFile,
  removeSiteFile,
} from '../modules/sites/api';
import { uploadSharedFile } from '../modules/orders/api';
import { listSchedulesForPartner } from '../modules/order-schedules/api';
import { downloadCsv, escapeCsvField } from '../utils/csv';
import { listCollectionPointsForPartner } from '../modules/collection-points/api';
import SitesMapView from '../components/SitesMapView';
import CollectionPointsSection from '../components/CollectionPointsSection';
import LinkedTicketsPanel from '../components/LinkedTicketsPanel';

// Format a Date | string | null as "DD MMM YYYY" or a dash. Used for
// the "last seen" column in the Discover modal — operators want to
// spot stale clients no longer worth onboarding.
function formatDate(d) {
  if (!d) return '—';
  const dj = dayjs(d);
  return dj.isValid() ? dj.format('DD MMM YYYY') : '—';
}

export default function PartnerSites({ partner }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // CT request (Aug 17): Export CSV of the full filtered catalog (the list
  // endpoint returns ALL sites — pagination is client-side only). Page size
  // is governed by the gear (ColumnConfigEditor), per the house rule.
  const handleExportCsv = () => {
    const rows = filteredSites.map((site) => [
      site.name,
      site.address,
      site.phone || '',
      site.notes || '',
      site.lat ?? '',
      site.lng ?? '',
      (schedulesBySite.get(site.id) || []).length,
      (pointsBySite.get(site.id) || []).length,
      (pointsBySite.get(site.id) || []).map((cp) => cp.name).join('; '),
      site.isArchived ? 'yes' : 'no',
    ]);
    // downloadCsv takes PRE-ESCAPED lines — every cell must go through
    // escapeCsvField (commas/quotes quoted, newlines flattened) or a note
    // with a comma splits the row (reported on the EPI export, Aug 18).
    const header = [
      'Name',
      'Address',
      'Phone',
      'Notes',
      'Lat',
      'Lng',
      'Schedules',
      'Collection points',
      'Box names',
      'Archived',
    ];
    downloadCsv(
      [header, ...rows].map((row) => row.map(escapeCsvField).join(',')),
      `sites-${(partner?.code || partner?.name || partner?.id || 'partner').toString().replace(/[^a-z0-9-]/gi, '_')}-${new Date().toISOString().slice(0, 10)}.csv`,
    );
  };
  // Structured filters (drawer). The text search stays server-side (`q`);
  // these are client-side over the loaded catalog.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  // OVO-169 — Catalog table vs. Map view (same partner-scoped sites).
  const [view, setView] = useState('catalog');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const [discoverOpen, setDiscoverOpen] = useState(false);

  // OVO-101 — merge: row-selection + modal state. The catalog table
  // shows a checkbox per row when there's >1 site; toolbar "Merge"
  // button enables once ≥2 rows are checked.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  // OVO-142 — "Suggest merges" modal (heuristic near-duplicate clusters).
  const [suggestOpen, setSuggestOpen] = useState(false);

  // OVO-113 — all schedules for the partner, grouped by site_id for
  // the expand-row UI. One fetch covers every site so opening the
  // chevron is instant + the per-site "N schedules" badge is cheap.
  const [schedules, setSchedules] = useState([]);
  // OVO-206 — collection points for the partner, grouped by site_id for the
  // per-site "N boxes" column. One fetch covers every site.
  const [collectionPoints, setCollectionPoints] = useState([]);

  // ─── Loaders ──────────────────────────────────────────────────────────

  const reload = useCallback(
    async (q) => {
      if (!partner?.id) return;
      setLoading(true);
      try {
        const [sitesRes, schedulesRes, pointsRes] = await Promise.all([
          listSitesForPartner(partner.id, q),
          listSchedulesForPartner(partner.id),
          listCollectionPointsForPartner(partner.id),
        ]);
        setSites(sitesRes.response || []);
        setSchedules(Array.isArray(schedulesRes.response) ? schedulesRes.response : []);
        setCollectionPoints(Array.isArray(pointsRes.response) ? pointsRes.response : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load sites');
      } finally {
        setLoading(false);
      }
    },
    [partner?.id],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  // OVO-113 — group schedules by site_id once; the per-row lookup
  // becomes O(1) and the count badge is cheap.
  //
  // A schedule appears under EACH Site it touches (pickup AND/OR
  // delivery). For STER's lab↔clinic schedules, both sites' expand-
  // rows surface the same Schedule with a role tag — the operator
  // can edit from either entry point.
  const schedulesBySite = useMemo(() => {
    const m = new Map();
    const push = (siteId, schedule) => {
      if (!siteId) return;
      const arr = m.get(siteId) || [];
      // De-dup: a schedule might have the SAME Site on both sides
      // (rare but possible — single-site round trip).
      if (!arr.find((x) => x.id === schedule.id)) arr.push(schedule);
      m.set(siteId, arr);
    };
    for (const s of schedules) {
      push(s.pickupSiteId, s);
      push(s.deliverySiteId, s);
    }
    return m;
  }, [schedules]);

  // OVO-206 — collection points grouped by site_id for the "N boxes" column.
  const pointsBySite = useMemo(() => {
    const m = new Map();
    for (const p of collectionPoints) {
      const arr = m.get(p.siteId) || [];
      arr.push(p);
      m.set(p.siteId, arr);
    }
    return m;
  }, [collectionPoints]);

  const filteredSites = useMemo(
    () =>
      sites.filter((site) => {
        const yn = (cond, want) => (want === 'yes' ? cond : !cond);
        if (filters.hasBoxes && !yn((pointsBySite.get(site.id) || []).length > 0, filters.hasBoxes)) return false;
        if (filters.hasSchedules && !yn((schedulesBySite.get(site.id) || []).length > 0, filters.hasSchedules))
          return false;
        // The route-2197 lesson: a coordinate-less site silently blocks whole
        // days of optimization, so it must be findable in one click.
        if (filters.missingCoords && !yn(site.lat == null || site.lng == null, filters.missingCoords)) return false;
        return true;
      }),
    [sites, filters, pointsBySite, schedulesBySite],
  );

  // OVO-101 — `?focusSiteId=N` on the URL (typically set by the
  // OrdersTable Site-cell link) auto-opens the editor for that site
  // once the catalog has loaded. The param is consumed once (we wipe
  // the search after handling so a tab-switch doesn't re-open it).
  const location = useLocation();
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const focusId = search.get('focusSiteId');
    if (!focusId || sites.length === 0) return;
    const target = sites.find((s) => String(s.id) === String(focusId));
    if (target) {
      // openEditor is defined below; pull it via a fresh closure so
      // we don't trigger re-renders on every site/sites change.
      setEditing(target);
      form.setFieldsValue({
        ...target,
        siteAddress: { address: target.address, latLng: { lat: target.lat, lng: target.lng } },
      });
      setEditorOpen(true);
    }
  }, [sites, location.search, form]);

  // ─── Editor modal ─────────────────────────────────────────────────────

  const openEditor = (site) => {
    setEditing(site);
    if (site) {
      form.setFieldsValue({
        ...site,
        siteAddress: { address: site.address, latLng: { lat: site.lat, lng: site.lng } },
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isArchived: false });
    }
    setEditorOpen(true);
  };

  const handleSave = async () => {
    try {
      const rawValues = await form.validateFields();
      // OVO-101 site editor — the Address field is backed by AddressInput
      // (Google Places autocomplete), which stores its value as the composite
      // `{ address, latLng }` shape used across the app (see OrderForm's
      // pickupAddress/deliveryAddress). Flatten it back to the Site's plain
      // `address` string before hitting the API; `lat`/`lng` are the separate,
      // still-editable number fields (auto-filled on select, hand-tweakable).
      const { siteAddress, ...values } = rawValues;
      values.address = siteAddress?.address;
      if (editing) {
        const { error } = await updateSite(editing.id, values);
        if (error) throw new Error(error.message || 'Update failed');
        message.success('Site updated');
      } else {
        const { error } = await createSite(partner.id, values);
        if (error) throw new Error(error.message || 'Create failed');
        message.success('Site created');
      }
      setEditorOpen(false);
      setEditing(null);
      form.resetFields();
      reload(search);
    } catch (e) {
      if (e?.errorFields) return; // AntD form validation
      message.error(e?.message || 'Failed to save site');
    }
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await deleteSite(id);
      if (error) throw new Error(error.message || 'Delete failed');
      message.success('Site deleted');
      reload(search);
    } catch (e) {
      message.error(e?.message || 'Failed to delete site');
    }
  };

  // OVO-148 — re-run the retroactive backfill across every Site of the
  // partner. Repairs history left unlinked by an address-format drift in
  // the order feed (Discover-schedules only sees Site-linked orders).
  const [backfilling, setBackfilling] = useState(false);
  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const { response, error } = await backfillPartnerSites(partner.id);
      if (error) throw new Error(error.message || 'Backfill failed');
      const linked = (response?.pickupUpdated || 0) + (response?.deliveryUpdated || 0);
      message.success(
        linked
          ? `Re-linked ${linked} order${linked === 1 ? '' : 's'} across ${response.sites} sites`
          : 'All orders were already linked — nothing to do',
      );
      reload(search);
    } catch (e) {
      message.error(e?.message || 'Failed to re-link orders');
    } finally {
      setBackfilling(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      const { response, error } = await bulkDeleteSites(partner.id, selectedRowKeys);
      if (error) throw new Error(error.message || 'Bulk delete failed');
      const n = response?.deleted ?? selectedRowKeys.length;
      message.success(`Deleted ${n} site${n === 1 ? '' : 's'}`);
      setSelectedRowKeys([]);
      reload(search);
    } catch (e) {
      message.error(e?.message || 'Failed to delete sites');
    }
  };

  // ─── Catalog columns ──────────────────────────────────────────────────

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (v, r) => {
          const n = schedulesBySite.get(r.id)?.length || 0;
          return (
            <>
              <strong>{v}</strong>
              {r.isArchived && (
                <Tag color="default" style={{ marginLeft: 8 }}>
                  archived
                </Tag>
              )}
              {/* OVO-113 — "N schedules" badge surfaces recurring orders
                  attached to this Site. Clicking the row's chevron expands
                  the per-site SiteSchedules editor. */}
              {n > 0 && (
                <Tag color="purple" style={{ marginLeft: 8 }}>
                  {n} schedule{n > 1 ? 's' : ''}
                </Tag>
              )}
            </>
          );
        },
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        ellipsis: true,
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        width: 160,
        render: (v) => v || '—',
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        ellipsis: true,
        render: (v) =>
          v ? (
            <Tooltip title={v}>
              <span style={{ color: 'var(--text-secondary)' }}>{v}</span>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        // OVO-206 — collection boxes ("Caisses") at this Site. Count badge with
        // the box names on hover; managed in the Site editor or /admin/collection-points.
        title: 'Collection points',
        key: 'collectionPoints',
        width: 160,
        render: (_, r) => {
          const pts = pointsBySite.get(r.id) || [];
          if (pts.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
          return (
            <Tooltip title={pts.map((p) => (p.externalRef ? `${p.externalRef} · ${p.name}` : p.name)).join(', ')}>
              <Tag color="geekblue">
                {pts.length} box{pts.length > 1 ? 'es' : ''}
              </Tag>
            </Tooltip>
          );
        },
      },
      {
        title: '',
        key: 'actions',
        width: 150,
        render: (_, r) => (
          <Space>
            {/* OVO-101 — cross-link to the Orders tab pre-filtered on
                this Site. The Orders tab reads `?siteId=N` from the URL
                and prefills both the OrdersFilter and the query. */}
            <Link to={`/admin/partners/${partner?.id}/orders?siteId=${r.id}`}>
              <Button size="small" icon={<UnorderedListOutlined />} title="View orders for this site" />
            </Link>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(r)} />
            <Popconfirm
              title="Delete this site?"
              description="Orders that referenced this site will keep their snapshot — the FK becomes null."
              onConfirm={() => handleDelete(r.id)}
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [partner?.id, schedulesBySite, pointsBySite],
  );

  // ─── Render ───────────────────────────────────────────────────────────

  // Same Settings drawer as every other list (columns + page size). The
  // table hard-coded pageSize 30, which AntD treats as a controlled value,
  // so the bottom size selector could never take effect.
  const SITE_FIXED_KEYS = ['actions'];
  const columnConfig = useColumnConfig('sites-columns', columns, SITE_FIXED_KEYS);

  return (
    <div className="partner-sites">
      <Card
        title="Sites (Address Book)"
        size="small"
        extra={
          <Space>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search name / address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => reload(search)}
              onBlur={() => reload(search)}
              style={{ width: 240 }}
            />
            <Button icon={<DownloadOutlined />} onClick={handleExportCsv} disabled={loading}>
              Export CSV
            </Button>
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} site${selectedRowKeys.length === 1 ? '' : 's'}?`}
              description="Orders that referenced any of these sites keep their snapshot — the FK becomes null."
              onConfirm={handleBulkDelete}
              okButtonProps={{ danger: true }}
              disabled={selectedRowKeys.length === 0}
            >
              <Button danger icon={<DeleteOutlined />} disabled={selectedRowKeys.length === 0}>
                Delete ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
            <Button
              icon={<MergeCellsOutlined />}
              disabled={selectedRowKeys.length < 2}
              onClick={() => setMergeOpen(true)}
            >
              Merge ({selectedRowKeys.length})
            </Button>
            <Button icon={<MergeCellsOutlined />} onClick={() => setSuggestOpen(true)}>
              Suggest merges
            </Button>
            <Button icon={<CompassOutlined />} onClick={() => setDiscoverOpen(true)}>
              Discover Sites
            </Button>
            <Tooltip title="Re-link historical orders to the catalog (catches address-format drift). Run this if Discover schedules finds nothing despite months of orders.">
              <Button icon={<LinkOutlined />} loading={backfilling} onClick={handleBackfill}>
                Re-link orders
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
              Add site
            </Button>
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            <Button
              type="text"
              title="Settings"
              onClick={columnConfig.openDrawer}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </Space>
        }
      >
        {/* OVO-169 — Catalog/Map switcher. Lives in the body (not the Card
            title) because the header `extra` toolbar is crowded enough that
            AntD ellipsis-clipped a title-embedded Segmented out of view. */}
        <div style={{ marginBottom: 12 }}>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: 'Catalog', value: 'catalog', icon: <UnorderedListOutlined /> },
              { label: 'Map', value: 'map', icon: <EnvironmentOutlined /> },
            ]}
          />
        </div>
        {view === 'map' ? (
          // OVO-169 — map of the (search-filtered) sites; click a pin → passages.
          <SitesMapView sites={sites} />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Sites are partner-scoped recurring locations"
              description="At order-create time, the planner exact-matches (name, address) against this catalog and stamps the Site FK on the order. Tick ≥2 rows to merge near-duplicates into one canonical Site."
            />
            <Table
              dataSource={filteredSites}
              columns={columnConfig.visibleColumns}
              rowKey="id"
              size="small"
              loading={loading}
              pagination={{ pageSize: columnConfig.pageSize, showSizeChanger: false, hideOnSinglePage: true }}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }}
              // OVO-134 — the whole row opens the editor (not just the Edit
              // button). Clicks on the action buttons, the View-orders link, the
              // selection checkbox, or a popconfirm are ignored so they keep their
              // own behavior.
              onRow={(record) => ({
                onClick: (e) => {
                  if (e.target.closest('button, a, input, .ant-checkbox, .ant-popover, .ant-popconfirm')) return;
                  openEditor(record);
                },
                style: { cursor: 'pointer' },
              })}
              // OVO-113 — the "N schedules" badge on the Name column links
              // operators to the dedicated Recurring tab. The per-Site
              // expand-row pattern was removed in favor of a flat list at
              // /admin/partners/:id/schedules — easier to scan when a
              // partner has many schedules.
              locale={{
                emptyText:
                  'No sites yet. Click "Discover Sites" to propose Sites from this partner\'s existing orders, or "Add site" to create one manually.',
              }}
            />
            <TableFilterDrawer
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              values={filters}
              onApply={setFilters}
              fields={[
                {
                  name: 'hasBoxes',
                  label: 'Collection boxes',
                  placeholder: 'Any',
                  options: [
                    { value: 'yes', label: 'Has boxes' },
                    { value: 'no', label: 'No boxes' },
                  ],
                },
                {
                  name: 'hasSchedules',
                  label: 'Recurring schedules',
                  placeholder: 'Any',
                  options: [
                    { value: 'yes', label: 'Has schedules' },
                    { value: 'no', label: 'No schedules' },
                  ],
                },
                {
                  name: 'missingCoords',
                  label: 'Coordinates',
                  placeholder: 'Any',
                  options: [
                    { value: 'yes', label: 'Missing coordinates' },
                    { value: 'no', label: 'Has coordinates' },
                  ],
                },
              ]}
            />
            <ColumnConfigDrawer
              open={columnConfig.drawerOpen}
              onClose={columnConfig.closeDrawer}
              columns={columnConfig.orderedAllColumns}
              hidden={columnConfig.hidden}
              fixedKeys={SITE_FIXED_KEYS}
              onToggle={columnConfig.toggleColumn}
              onMove={columnConfig.moveColumn}
              onReset={columnConfig.resetConfig}
              pageSize={columnConfig.pageSize}
              onPageSizeChange={columnConfig.setPageSize}
            />
          </>
        )}
      </Card>

      {/* Add / edit modal */}
      <Modal
        title={editing ? `Edit site — ${editing.name}` : 'Add site'}
        open={editorOpen}
        onCancel={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onOk={handleSave}
        okText={editing ? 'Save' : 'Create'}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Required' }]}
            extra="The client/location name as it appears on orders (e.g. 'Hôpital Jules-Gonin')"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="siteAddress"
            label="Address"
            extra="Start typing to search — selecting a suggestion fills in the coordinates below."
            rules={[
              {
                // Coordinates are required, not cosmetic: a site saved without
                // them produces orders with NULL lat/lng, and one such order
                // makes Google reject the WHOLE day's optimize. Typing an
                // address without picking a suggestion leaves them empty, so
                // the address field itself enforces both.
                validator: (_, value) => {
                  if (!value?.address || !String(value.address).trim()) {
                    return Promise.reject(new Error('Required'));
                  }
                  const { lat, lng } = form.getFieldsValue(['lat', 'lng']);
                  const ok = (v) => typeof v === 'number' && Number.isFinite(v);
                  return ok(lat) && ok(lng)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Pick the address from the suggestions so the coordinates are filled in'),
                      );
                },
              },
            ]}
          >
            <AddressInput
              placeholder="Address"
              onSelect={(value) =>
                form.setFieldsValue({ siteAddress: value, lat: value.latLng?.lat, lng: value.latLng?.lng })
              }
            />
          </Form.Item>
          <Space style={{ display: 'flex' }} size="small">
            <Form.Item name="lat" label="Lat" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
              <InputNumber style={{ width: '100%' }} step="0.000001" />
            </Form.Item>
            <Form.Item name="lng" label="Lng" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
              <InputNumber style={{ width: '100%' }} step="0.000001" />
            </Form.Item>
          </Space>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isArchived" label="Archived" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>

        {/* OVO-103 Phase 2 — per-Site hint photos. Only shown for
            existing sites (need an id to attach photos to). New sites
            need to be saved first, then re-opened for photo upload. */}
        {editing?.id && <SitePhotosSection siteId={editing.id} />}
        {editing?.id && <CollectionPointsSection siteId={editing.id} partnerId={partner?.id} />}
        {/* #132 — tickets about this site (and its boxes) */}
        {editing?.id && (
          <LinkedTicketsPanel
            filter={{ siteId: editing.id }}
            prefill={{ siteId: editing.id, siteLabel: editing.name, partnerId: partner?.id }}
            style={{ marginTop: 12 }}
          />
        )}
      </Modal>

      {/* Discover modal */}
      <DiscoverSitesModal
        partnerId={partner?.id}
        open={discoverOpen}
        onClose={() => setDiscoverOpen(false)}
        onSaved={() => {
          setDiscoverOpen(false);
          reload(search);
        }}
      />

      {/* Merge modal */}
      <MergeSitesModal
        partnerId={partner?.id}
        open={mergeOpen}
        selectedSites={sites.filter((s) => selectedRowKeys.includes(s.id))}
        onClose={() => setMergeOpen(false)}
        onMerged={() => {
          setMergeOpen(false);
          setSelectedRowKeys([]);
          reload(search);
        }}
      />

      {/* OVO-142 — Suggest merges modal */}
      <SuggestMergesModal
        partnerId={partner?.id}
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        onMerged={() => reload(search)}
      />
    </div>
  );
}

// ─── Suggest merges modal (OVO-142) ────────────────────────────────────────
//
// Fetches heuristic near-duplicate clusters (same normalized address + a
// compatible name), lists each with a radio to pick the canonical Site
// (pre-selected = the backend's suggestion: most linked orders), and merges
// a cluster in one click via the existing mergeSites endpoint.
function SuggestMergesModal({ partnerId, open, onClose, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [primaryByIdx, setPrimaryByIdx] = useState({});
  const [mergingIdx, setMergingIdx] = useState(null);

  useEffect(() => {
    if (!open || !partnerId) return;
    setLoading(true);
    getMergeSuggestions(partnerId)
      .then(({ response }) => {
        const list = response?.suggestions || [];
        setClusters(list);
        const init = {};
        list.forEach((c, i) => (init[i] = c.suggestedPrimaryId));
        setPrimaryByIdx(init);
      })
      .catch(() => message.error('Could not load merge suggestions'))
      .finally(() => setLoading(false));
  }, [open, partnerId]);

  const mergeCluster = async (idx) => {
    const cluster = clusters[idx];
    const primaryId = primaryByIdx[idx];
    const mergeIds = cluster.members.map((m) => m.id).filter((id) => id !== primaryId);
    if (mergeIds.length === 0) return;
    setMergingIdx(idx);
    try {
      const { error } = await mergeSites(partnerId, { primaryId, mergeIds });
      if (error) {
        message.error(error.message || 'Merge failed');
        return;
      }
      message.success(`Merged ${mergeIds.length + 1} sites into 1`);
      // Drop the merged cluster from the list; re-index the selections.
      const remaining = clusters.filter((_, i) => i !== idx);
      setClusters(remaining);
      const reindexed = {};
      remaining.forEach((c, i) => {
        const oldIdx = i < idx ? i : i + 1;
        reindexed[i] = primaryByIdx[oldIdx] ?? c.suggestedPrimaryId;
      });
      setPrimaryByIdx(reindexed);
      onMerged?.();
    } catch (e) {
      message.error(e?.message || 'Merge failed');
    } finally {
      setMergingIdx(null);
    }
  };

  return (
    <Modal
      title="Suggested merges"
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={720}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : clusters.length === 0 ? (
        <Empty description="No near-duplicate sites found 🎉" />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${clusters.length} group${clusters.length === 1 ? '' : 's'} of near-duplicates found`}
            description="Each group shares the same address (ignoring “, CH” etc.) and a compatible name. Pick the Site to keep — the others' orders are reassigned to it, then they're deleted."
          />
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {clusters.map((cluster, idx) => (
              <Card
                key={cluster.suggestedPrimaryId + '-' + idx}
                size="small"
                title={
                  <span style={{ fontWeight: 500 }}>
                    {cluster.members.length} sites ·{' '}
                    <span style={{ color: 'var(--text-muted)' }}>{cluster.addressKey}</span>
                  </span>
                }
                extra={
                  <Button
                    type="primary"
                    size="small"
                    loading={mergingIdx === idx}
                    icon={<MergeCellsOutlined />}
                    onClick={() => mergeCluster(idx)}
                  >
                    Merge {cluster.members.length} → 1
                  </Button>
                }
              >
                <Radio.Group
                  value={primaryByIdx[idx]}
                  onChange={(e) => setPrimaryByIdx((prev) => ({ ...prev, [idx]: e.target.value }))}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    {cluster.members.map((m) => (
                      <Radio key={m.id} value={m.id} style={{ width: '100%' }}>
                        <span>
                          <b>{m.name}</b>
                          {m.id === cluster.suggestedPrimaryId && (
                            <Tag color="blue" style={{ marginLeft: 6 }}>
                              suggested
                            </Tag>
                          )}
                          <span style={{ color: 'var(--text-muted)' }}> · {m.orderCount} orders</span>
                          <br />
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{m.address}</span>
                        </span>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                <div style={{ color: '#aaa', fontSize: 11, marginTop: 6 }}>
                  The selected Site is kept; the others are merged into it.
                </div>
              </Card>
            ))}
          </Space>
        </>
      )}
    </Modal>
  );
}

// ─── Discover modal ──────────────────────────────────────────────────────
//
// Lazy-loaded on open. Shows the candidates table with:
//   - row checkboxes (all checked by default)
//   - editable Name + Address inputs (operator can fix typos / strip
//     residual partner-code prefix that the server didn't catch)
//   - visit counts (pickup / delivery / total / distinct days)
//   - last seen (so operators can deprioritize stale clients)
// Clicking "Add selected" POSTs the validated subset via bulk-create.
function DiscoverSitesModal({ partnerId, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);

  // Pull candidates from the server when the modal opens.
  useEffect(() => {
    if (!open || !partnerId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    discoverSites(partnerId)
      .then(({ response, error }) => {
        if (cancelled) return;
        if (error) {
          message.error(error.message || 'Discover failed');
          return;
        }
        setData(response);
        setRows(
          (response?.candidates || []).map((c, i) => ({
            ...c,
            key: i,
            // Each row carries its own editable copy of name + address;
            // the original `name` / `address` from the server are kept
            // as a tooltip via `serverName` / `serverAddress`.
            editName: c.name,
            editAddress: c.address,
            serverName: c.name,
            serverAddress: c.address,
            selected: true,
          })),
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, partnerId]);

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const selected = useMemo(() => rows.filter((r) => r.selected), [rows]);
  const allChecked = rows.length > 0 && rows.every((r) => r.selected);
  const someChecked = rows.some((r) => r.selected);

  const handleSave = async () => {
    if (selected.length === 0) {
      message.warning('Select at least one site to add');
      return;
    }
    // Pre-validate: trimmed name + address must be non-empty.
    const invalid = selected.find((r) => !(r.editName || '').trim() || !(r.editAddress || '').trim());
    if (invalid) {
      message.error(`Row "${invalid.editName || '(empty)'}" has an empty name or address`);
      return;
    }
    setSaving(true);
    try {
      const { response, error } = await bulkCreateSites(
        partnerId,
        selected.map((r) => {
          // Carry over the geocoded coords from the candidate's source
          // order(s) — orders are already geocoded by the time Discover
          // runs, so the new Site lands on the map without a re-geocode
          // round-trip. BUT only when the operator didn't edit the
          // address (an edited address invalidates the geocoded coords
          // — better to leave the Site without coords and let the
          // operator fix it later than to ship a wrong pin).
          const addressUnchanged = r.editAddress.trim() === r.serverAddress;
          return {
            name: r.editName.trim(),
            address: r.editAddress.trim(),
            lat: addressUnchanged ? r.lat ?? undefined : undefined,
            lng: addressUnchanged ? r.lng ?? undefined : undefined,
          };
        }),
      );
      if (error) throw new Error(error.message || 'Bulk save failed');
      const { created = 0, skipped = 0 } = response || {};
      if (created > 0) {
        message.success(
          `Added ${created} site${created === 1 ? '' : 's'}${skipped > 0 ? ` — ${skipped} skipped as duplicates` : ''}`,
        );
      } else if (skipped > 0) {
        message.info(`All ${skipped} selected sites already exist`);
      }
      onSaved?.();
    } catch (e) {
      message.error(e?.message || 'Failed to add sites');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={allChecked}
          indeterminate={!allChecked && someChecked}
          onChange={(e) => setRows((prev) => prev.map((r) => ({ ...r, selected: e.target.checked })))}
        />
      ),
      key: 'select',
      width: 40,
      render: (_, r) => (
        <Checkbox checked={r.selected} onChange={(e) => updateRow(r.key, { selected: e.target.checked })} />
      ),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <>
          <Input
            value={r.editName}
            onChange={(e) => updateRow(r.key, { editName: e.target.value })}
            size="small"
            disabled={!r.selected}
          />
          {r.rawSamples?.length > 0 && (
            <Tooltip
              title={
                <>
                  <div>Originally seen as:</div>
                  {r.rawSamples.map((s) => (
                    <div key={s} style={{ fontSize: 12 }}>
                      • {s}
                    </div>
                  ))}
                </>
              }
            >
              <Tag icon={<WarningOutlined />} color="orange" style={{ marginTop: 4, fontSize: 11 }}>
                partner-code stripped
              </Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: 'Address',
      key: 'address',
      render: (_, r) => (
        <Input
          value={r.editAddress}
          onChange={(e) => updateRow(r.key, { editAddress: e.target.value })}
          size="small"
          disabled={!r.selected}
        />
      ),
    },
    {
      title: 'Pickup',
      dataIndex: 'pickupVisits',
      key: 'pickupVisits',
      width: 70,
      align: 'right',
      sorter: (a, b) => a.pickupVisits - b.pickupVisits,
    },
    {
      title: 'Delivery',
      dataIndex: 'deliveryVisits',
      key: 'deliveryVisits',
      width: 80,
      align: 'right',
      sorter: (a, b) => a.deliveryVisits - b.deliveryVisits,
    },
    {
      title: 'Days',
      dataIndex: 'distinctDays',
      key: 'distinctDays',
      width: 70,
      align: 'right',
      sorter: (a, b) => a.distinctDays - b.distinctDays,
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      width: 120,
      render: formatDate,
      sorter: (a, b) =>
        (a.lastSeen ? new Date(a.lastSeen).getTime() : 0) - (b.lastSeen ? new Date(b.lastSeen).getTime() : 0),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Discover Sites — propose new sites from existing orders"
      width={1100}
      onOk={handleSave}
      okText={`Add ${selected.length} site${selected.length === 1 ? '' : 's'}`}
      okButtonProps={{ disabled: selected.length === 0 || saving, loading: saving }}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          data
            ? `Scanned ${data.totalOrdersScanned} order${
                data.totalOrdersScanned === 1 ? '' : 's'
              } · catalog already has ${data.catalogSize} site${
                data.catalogSize === 1 ? '' : 's'
              } · ${rows.length} new candidate${rows.length === 1 ? '' : 's'}`
            : "Scanning the partner's orders…"
        }
        description="Edit any row's name or address before saving — typos and residual partner-code prefixes can be cleaned up here once. Existing sites are filtered out automatically; if a duplicate sneaks through, it's silently skipped at save time."
      />
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="key"
        size="small"
        loading={loading}
        pagination={rows.length > 50 ? { pageSize: 50 } : false}
        locale={{
          emptyText: data ? 'No new candidates — the catalog already covers all observed pickup/delivery names.' : ' ',
        }}
      />
    </Modal>
  );
}

// ─── Merge modal ─────────────────────────────────────────────────────────
//
// Operator has ticked ≥2 sites in the catalog and clicked "Merge". The
// modal:
//   - lists the selected rows with a single radio across them — exactly
//     one must be the "primary" (= survivor)
//   - shows editable Name + Address fields pre-filled from the primary,
//     so the operator can canonicalize the identity inline (the original
//     near-duplicate names typically have small variations the operator
//     wants to consolidate at merge time, not as a separate edit pass)
//   - "Merge" button → POST .../sites/merge — backend transactionally
//     reassigns every order's pickup/deliverySiteId from each victim to
//     the primary, optionally renames the primary, then deletes victims
function MergeSitesModal({ partnerId, open, selectedSites, onClose, onMerged }) {
  const [primaryId, setPrimaryId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [saving, setSaving] = useState(false);

  // Whenever the operator (re-)opens the modal, default the primary to
  // the FIRST selected row + seed the name/address inputs from it.
  // Operator can switch primary anytime — clicking another radio
  // re-seeds the inputs with that row's identity.
  useEffect(() => {
    if (!open || !selectedSites?.length) return;
    const first = selectedSites[0];
    setPrimaryId(first.id);
    setEditName(first.name);
    setEditAddress(first.address);
  }, [open, selectedSites]);

  const handlePickPrimary = (id) => {
    const next = selectedSites.find((s) => s.id === id);
    if (!next) return;
    setPrimaryId(id);
    setEditName(next.name);
    setEditAddress(next.address);
  };

  const handleMerge = async () => {
    if (!primaryId) {
      message.warning('Pick one site as the primary');
      return;
    }
    const mergeIds = selectedSites.map((s) => s.id).filter((id) => id !== primaryId);
    if (mergeIds.length === 0) {
      message.warning('Need at least one site to merge into the primary');
      return;
    }
    setSaving(true);
    try {
      const primary = selectedSites.find((s) => s.id === primaryId);
      const cleanedName = (editName || '').trim();
      const cleanedAddress = (editAddress || '').trim();
      if (!cleanedName || !cleanedAddress) {
        message.error('Primary name and address are required');
        setSaving(false);
        return;
      }
      const payload = { primaryId, mergeIds };
      // Only send name/address if they actually changed (lets the
      // backend skip the UPDATE when the operator just wanted to
      // reassign orders without renaming).
      if (cleanedName !== primary.name) payload.name = cleanedName;
      if (cleanedAddress !== primary.address) payload.address = cleanedAddress;
      const { response, error } = await mergeSites(partnerId, payload);
      if (error) throw new Error(error.message || 'Merge failed');
      message.success(
        `Merged ${response?.mergedCount ?? mergeIds.length} site${
          (response?.mergedCount ?? mergeIds.length) === 1 ? '' : 's'
        } into "${cleanedName}" · ${response?.ordersReassigned ?? 0} order${
          (response?.ordersReassigned ?? 0) === 1 ? '' : 's'
        } reassigned`,
      );
      onMerged?.();
    } catch (e) {
      message.error(e?.message || 'Merge failed');
    } finally {
      setSaving(false);
    }
  };

  const victimCount = Math.max(0, (selectedSites?.length || 0) - 1);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Merge sites"
      width={820}
      onOk={handleMerge}
      okText={`Merge ${victimCount} into primary`}
      okButtonProps={{ disabled: !primaryId || victimCount === 0 || saving, loading: saving }}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="This will reassign every order's Site FK and delete the merged rows"
        description="Orders pointing at the non-primary rows will get their pickup/delivery Site FK rewritten to the primary. The free-text pickupName/deliveryName on each order is left untouched (so the order's snapshot of what it looked like at creation time is preserved)."
      />

      <div style={{ marginBottom: 12 }}>
        <strong>Selected sites</strong> — pick the survivor:
      </div>
      <Table
        size="small"
        dataSource={selectedSites}
        rowKey="id"
        pagination={false}
        columns={[
          {
            title: 'Primary',
            key: 'primary',
            width: 70,
            align: 'center',
            render: (_, r) => (
              <input
                type="radio"
                name="merge-primary"
                checked={r.id === primaryId}
                onChange={() => handlePickPrimary(r.id)}
              />
            ),
          },
          { title: 'Name', dataIndex: 'name', key: 'name' },
          { title: 'Address', dataIndex: 'address', key: 'address', ellipsis: true },
        ]}
      />

      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <strong>Primary identity</strong> — edit if you want to canonicalize at merge time:
      </div>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          addonBefore="Name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          disabled={!primaryId}
        />
        <Input.TextArea
          rows={2}
          value={editAddress}
          onChange={(e) => setEditAddress(e.target.value)}
          disabled={!primaryId}
          placeholder="Address"
        />
      </Space>
    </Modal>
  );
}

// ─── OVO-103 Phase 2 — per-Site hint photos ─────────────────────────────
//
// Photos uploaded here auto-flow to every order linked to this Site (now
// AND in the future). Mirrors the OrderMediaSection layout (Pickup +
// Delivery tabs) but talks to the Site-scoped endpoints. The upload
// pipeline reuses /common/shared-upload — same as OrderMediaSection.
//
// Backend semantics:
//   - Adding a SiteFile of type=X immediately fans out to every order
//     currently linked to this Site via the matching FK (pickupSiteId
//     for X=PICKUP_HINT, deliverySiteId for X=DELIVERY_HINT). The new
//     FileOrder rows appear on those orders' OrderMediaSection
//     without any extra refresh from the operator.
//   - Removing a SiteFile cleans up the matching FileOrder rows on
//     every linked order — the catalog is the source of truth.
//   - Future orders that resolve to this Site at create time pick up
//     all of its photos automatically (OrdersService.create →
//     SitesService.attachSiteMediaToOrder).
function SitePhotosSection({ siteId }) {
  const host = useSelector((s) => s.auth?.user?.host);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);

  const refresh = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    const { response, error } = await listSiteFiles(siteId);
    if (error) {
      message.error(error.message || 'Failed to load site photos');
    } else if (Array.isArray(response)) {
      setFiles(response);
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const g = { PICKUP_HINT: [], DELIVERY_HINT: [] };
    for (const f of files) {
      if (g[f.type]) g[f.type].push(f);
    }
    return g;
  }, [files]);

  const handleUpload = async (type, file) => {
    setUploadingType(type);
    try {
      const { response: uploaded, error: uErr } = await uploadSharedFile(file);
      if (uErr || !uploaded?.id) {
        message.error(uErr?.message || 'Upload failed');
        return false;
      }
      const { error: lErr } = await addSiteFile(siteId, uploaded.id, type);
      if (lErr) {
        message.error(lErr.message || 'Could not attach photo to site');
        return false;
      }
      message.success(
        type === 'PICKUP_HINT'
          ? 'Pickup photo added — will appear on every order picking up here'
          : 'Delivery photo added — will appear on every order delivering here',
      );
      await refresh();
      return false; // tell AntD Upload not to do its own request
    } catch (e) {
      message.error(e?.message || 'Upload failed');
      return false;
    } finally {
      setUploadingType(null);
    }
  };

  const handleDelete = async (siteFileId) => {
    const { error } = await removeSiteFile(siteFileId);
    if (error) {
      message.error(error.message || 'Could not remove photo');
      return;
    }
    message.success('Photo removed from site and all linked orders');
    refresh();
  };

  const renderTab = (type, label, subtitle) => {
    const items = grouped[type] || [];
    return {
      key: type,
      label: `${label} (${items.length})`,
      children: (
        <div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>{subtitle}</div>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(f) => handleUpload(type, f)}
            disabled={uploadingType === type}
          >
            <Button icon={<UploadOutlined />} loading={uploadingType === type} style={{ marginBottom: 12 }}>
              Upload {label.toLowerCase()} photo
            </Button>
          </Upload>
          {items.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No ${label.toLowerCase()} photos for this site yet`}
              style={{ paddingTop: 8, paddingBottom: 8 }}
            />
          ) : (
            <Image.PreviewGroup>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {items.map((f) => (
                  <div
                    key={f.id}
                    style={{
                      position: 'relative',
                      width: 120,
                      height: 120,
                      borderRadius: 6,
                      overflow: 'hidden',
                      border: '1px solid #eee',
                    }}
                  >
                    {host && f.file?.key ? (
                      <Image
                        src={`${host}/${f.file.key}`}
                        alt={f.type}
                        width={120}
                        height={120}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : null}
                    <Popconfirm
                      title="Remove this photo?"
                      description="Removes the photo from this site AND from every order currently linked to it. The original file stays in storage."
                      onConfirm={() => handleDelete(f.id)}
                      okText="Remove"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          zIndex: 2,
                          opacity: 0.92,
                        }}
                      />
                    </Popconfirm>
                  </div>
                ))}
              </div>
            </Image.PreviewGroup>
          )}
        </div>
      ),
    };
  };

  return (
    <Card
      size="small"
      title={
        <span>
          <PictureOutlined /> Photos for the driver
        </span>
      }
      style={{ marginTop: 16 }}
    >
      <Spin spinning={loading}>
        <Tabs
          items={[
            renderTab('PICKUP_HINT', 'Pickup', 'Shown on the driver app whenever this site is the pickup of an order.'),
            renderTab(
              'DELIVERY_HINT',
              'Delivery',
              'Shown on the driver app whenever this site is the delivery of an order.',
            ),
          ]}
        />
      </Spin>
    </Card>
  );
}
